import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { MainLayout } from '../../components/MainLayout';
import api from '../../lib/api';
import { getCurrentAccount } from '../../lib/auth';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Star, Loader2, RefreshCw, Download, Settings2, Zap, Flame, Droplet,
  Gauge, Award, Leaf, Clock, AlertTriangle, PlugZap, Wand2, FlaskConical, Rocket,
  CheckCircle2, Info,
} from 'lucide-react';
import { ScoreEligibilityBadge } from '../../components/energyStar/ReadinessUI';
import { filterCount } from '../../components/energyStar/readiness';

const fmt = (n, d = 0) => (n === null || n === undefined ? '—'
  : Number(n).toLocaleString(undefined, { maximumFractionDigits: d }));

// ENERGY STAR score: higher is better (1–100). 75+ = certifiable.
const scoreTone = (s) =>
  s >= 75 ? { text: 'text-emerald-700', bg: 'bg-emerald-50', ring: 'ring-emerald-200', dot: 'bg-emerald-500', label: 'Certifiable' }
  : s >= 50 ? { text: 'text-amber-700', bg: 'bg-amber-50', ring: 'ring-amber-200', dot: 'bg-amber-500', label: 'Average' }
  : { text: 'text-rose-700', bg: 'bg-rose-50', ring: 'ring-rose-200', dot: 'bg-rose-500', label: 'Below median' };

const COMMODITIES = [
  { key: 'electricity', label: 'Electricity', icon: Zap },
  { key: 'gas', label: 'Gas', icon: Flame },
  { key: 'water', label: 'Water', icon: Droplet },
];

const STATUS_META = {
  available: { icon: Award, text: 'text-emerald-700', label: 'Metrics live' },
  pending: { icon: Clock, text: 'text-amber-700', label: 'Metrics pending' },
  not_connected: { icon: PlugZap, text: 'text-muted-foreground', label: 'Not connected' },
  not_mapped: { icon: Wand2, text: 'text-muted-foreground', label: 'Not created in ESPM' },
  error: { icon: AlertTriangle, text: 'text-rose-700', label: 'ESPM error' },
};

export default function EnergyStarReport() {
  const navigate = useNavigate();
  const acct = getCurrentAccount();
  const [env, setEnv] = useState('test');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!acct?.id) return;
    setLoading(true);
    try {
      const { data } = await api.get('/energy-star/report', { params: { account_id: acct.id, environment: env } });
      setData(data);
      if (data?.summary?.environment) setEnv(data.summary.environment);
    } catch (e) { setData(null); } finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [acct?.id, env]);

  useEffect(() => { load(); }, [load]);

  const summary = data?.summary;
  const props = data?.properties || [];

  const exportCsv = () => {
    const head = ['Property', 'City', 'State', 'ESPM Property ID', 'Representation',
      'Elec months', 'Gas months', 'Water months', 'ENERGY STAR Score', 'Site EUI',
      'Source EUI', 'GHG (MT CO2e)', 'Period ending', 'Metrics status'];
    const rows = props.map((p) => [
      p.property_name, p.city || '', p.state || '', p.espm_property_id || '', p.representation,
      p.meters?.electricity?.months_pushed ?? '', p.meters?.gas?.months_pushed ?? '',
      p.meters?.water?.months_pushed ?? '',
      p.metrics?.score ?? '', p.metrics?.site_eui ?? '', p.metrics?.source_eui ?? '',
      p.metrics?.ghg_mt ?? '', p.metrics?.period_ending ?? '', p.metrics_status,
    ]);
    const csv = [head, ...rows].map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url; a.download = `energy-star-report-${env}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <MainLayout>
      <div className="p-6 max-w-[1400px] mx-auto" data-testid="energy-star-report-page">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <div className="flex items-center gap-3">
            <Star className="h-7 w-7 text-amber-500" />
            <div>
              <h1 className="text-2xl font-black tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>Energy Star Reporting</h1>
              <p className="text-sm text-muted-foreground">ENERGY STAR® sync status & live scores per property</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* env toggle */}
            <div className="flex border border-border rounded-none overflow-hidden" data-testid="esr-env-toggle">
              <button
                onClick={() => setEnv('test')}
                className={`flex items-center gap-1.5 px-3 py-2 text-xs font-semibold ${env === 'test' ? 'bg-amber-500 text-white' : 'text-muted-foreground hover:bg-muted'}`}
                data-testid="esr-env-test"
              ><FlaskConical className="h-3.5 w-3.5" /> Sandbox</button>
              <button
                onClick={() => setEnv('prod')}
                className={`flex items-center gap-1.5 px-3 py-2 text-xs font-semibold ${env === 'prod' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'}`}
                data-testid="esr-env-prod"
              ><Rocket className="h-3.5 w-3.5" /> Production</button>
            </div>
            <Button variant="outline" className="rounded-none h-9" onClick={exportCsv} disabled={!props.length} data-testid="esr-export-csv">
              <Download className="h-4 w-4 mr-2" /> CSV
            </Button>
            <Button variant="ghost" size="icon" className="rounded-none h-9 w-9" onClick={load} data-testid="esr-refresh">
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            <Button variant="outline" className="rounded-none h-9" onClick={() => navigate('/settings?tab=energy-star')} data-testid="esr-configure">
              <Settings2 className="h-4 w-4 mr-2" /> Configure
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24 text-muted-foreground">
            <Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading ENERGY STAR report…
          </div>
        ) : !summary ? (
          <div className="text-center py-24 text-muted-foreground" data-testid="esr-empty">Unable to load the report.</div>
        ) : (
          <>
            {/* Connection banner */}
            {!summary.connected && (
              <div className="flex items-start gap-2 border border-amber-500/40 bg-amber-50 dark:bg-amber-950/20 p-3 rounded-none mb-5" data-testid="esr-not-connected-banner">
                <PlugZap className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
                <div className="text-sm">
                  <b>{env === 'test' ? 'Sandbox' : 'Production'} not connected.</b> Live ENERGY STAR scores can't be pulled until you connect this account.{' '}
                  <button className="underline text-amber-700 font-medium" onClick={() => navigate('/settings?tab=energy-star')}>Connect it in Settings →</button>
                </div>
              </div>
            )}

            {/* Readiness call-to-action strip (shares Settings' readiness engine) */}
            {summary.readiness_counts && (
              <ReadinessStrip counts={summary.readiness_counts} total={summary.candidates_total}
                env={env} navigate={navigate} />
            )}

            {/* Summary KPI strip */}
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4 mb-6" data-testid="esr-summary">
              <SummaryStat testid="esr-stat-score" icon={Award} label="Avg ENERGY STAR score"
                value={summary.avg_score ?? '—'} sub={summary.avg_score != null ? `${summary.certifiable} certifiable (75+)` : 'no scores yet'} tone={summary.avg_score != null ? scoreTone(summary.avg_score).text : ''} />
              <SummaryStat testid="esr-stat-live" icon={Gauge} label="Metrics live"
                value={`${summary.metrics_available}/${summary.properties_mapped}`} sub={`${summary.metrics_pending} pending`} />
              <SummaryStat testid="esr-stat-completeness" icon={Gauge} label="Months loaded (no miss)"
                value={`${summary.completeness_pct}%`}
                tone={summary.status === 'green' ? 'text-emerald-700' : summary.status === 'amber' ? 'text-amber-700' : 'text-rose-700'}
                sub={`${fmt(summary.loaded_month_reads)}/${fmt(summary.expected_month_reads)} month-reads`} />
              <SummaryStat testid="esr-stat-meters" icon={Zap} label="Active meters"
                value={`${summary.active_meters}/${summary.mapped_meters}`} sub="pushing data" />
              <SummaryStat testid="esr-stat-ghg" icon={Leaf} label="GHG (scored props)"
                value={summary.total_ghg_mt != null ? fmt(summary.total_ghg_mt) : '—'} sub="Metric Tons CO₂e" />
              <SummaryStat testid="esr-stat-props" icon={Star} label="Mapped properties"
                value={`${summary.properties_mapped}/${summary.properties_total}`} sub={env === 'test' ? 'Sandbox' : 'Production'} />
            </div>

            {/* Per-property cards */}
            {!props.length ? (
              <div className="text-center py-16 border border-dashed border-border text-muted-foreground" data-testid="esr-no-props">
                No properties mapped in this environment yet. <button className="underline text-primary" onClick={() => navigate('/settings?tab=energy-star')}>Set up mappings →</button>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="esr-property-grid">
                {props.map((p) => <PropertyCard key={p.site_uid} p={p} navigate={navigate} />)}
              </div>
            )}
          </>
        )}
      </div>
    </MainLayout>
  );
}

function SummaryStat({ testid, icon: Icon, label, value, sub, tone = '' }) {
  return (
    <div className="border border-border bg-card p-4" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        <Icon className="h-3.5 w-3.5" /> {label}
      </div>
      <div className={`text-3xl font-black tabular-nums mt-1 ${tone}`} style={{ fontFamily: 'Chivo, sans-serif' }}>{value}</div>
      <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div>
    </div>
  );
}

function ReadinessStrip({ counts, total, env, navigate }) {
  const ready = filterCount(counts, 'ready');
  const needs = filterCount(counts, 'needs');
  const connected = filterCount(counts, 'connected');
  const pills = [
    { key: 'ready', label: 'Ready to connect', n: ready, cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', Icon: CheckCircle2 },
    { key: 'needs', label: 'Need information', n: needs, cls: 'bg-amber-50 text-amber-700 border-amber-200', Icon: AlertTriangle },
    { key: 'connected', label: 'Connected', n: connected, cls: 'bg-sky-50 text-sky-700 border-sky-200', Icon: PlugZap },
  ];
  return (
    <div className="flex items-center justify-between flex-wrap gap-3 border border-border bg-card p-4 mb-6" data-testid="esr-readiness-strip">
      <div className="flex items-center gap-3 flex-wrap">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
          <Info className="h-3.5 w-3.5" /> Portfolio readiness ({total || 0} properties)
        </span>
        <div className="flex items-center gap-2 flex-wrap">
          {pills.map(({ key, label, n, cls, Icon }) => (
            <span key={key} className={`inline-flex items-center gap-1.5 border rounded-none px-2.5 py-1 text-xs font-semibold ${cls}`} data-testid={`esr-readiness-${key}`}>
              <Icon className="h-3.5 w-3.5" /> {n} {label}
            </span>
          ))}
        </div>
      </div>
      {(ready > 0 || needs > 0) && (
        <Button size="sm" className="rounded-none" onClick={() => navigate('/settings?tab=energy-star')} data-testid="esr-readiness-cta">
          <Wand2 className="h-4 w-4 mr-2" /> {ready > 0 ? `Connect ${ready} ready propert${ready === 1 ? 'y' : 'ies'}` : 'Fix property details'}
        </Button>
      )}
    </div>
  );
}

function PropertyCard({ p, navigate }) {
  const sm = STATUS_META[p.metrics_status] || STATUS_META.error;
  const SIcon = sm.icon;
  const m = p.metrics;
  const tone = m?.score != null ? scoreTone(m.score) : null;

  return (
    <div className="border border-border bg-card" data-testid={`esr-card-${p.site_uid}`}>
      {/* header */}
      <div className="flex items-start justify-between p-4 border-b border-border">
        <div>
          <div className="font-bold text-lg leading-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>{p.property_name}</div>
          <div className="text-xs text-muted-foreground mt-0.5">{[p.city, p.state].filter(Boolean).join(', ') || '—'}</div>
          <div className="flex items-center gap-2 mt-1.5">
            {p.espm_property_id && <span className="font-mono text-[10px] text-muted-foreground">ESPM #{p.espm_property_id}</span>}
            <Badge variant="outline" className="rounded-none text-[10px]" data-testid={`esr-rep-${p.site_uid}`}>
              {p.representation_key === 'common' ? 'Common Area Only' : 'Whole Property'}
            </Badge>
            <ScoreEligibilityBadge value={p.score_eligibility} />
          </div>
        </div>
        <span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold ${sm.text}`} data-testid={`esr-status-${p.site_uid}`}>
          <SIcon className="h-4 w-4" /> {sm.label}
        </span>
      </div>

      {/* metrics */}
      {p.metrics_status === 'available' && m ? (
        <div className="grid grid-cols-4 divide-x divide-border border-b border-border" data-testid={`esr-metrics-${p.site_uid}`}>
          <div className={`p-4 text-center ${tone.bg}`}>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Score</div>
            <div className={`text-4xl font-black ${tone.text}`} style={{ fontFamily: 'Chivo, sans-serif' }} data-testid={`esr-score-${p.site_uid}`}>{m.score ?? '—'}</div>
            <div className={`text-[10px] font-semibold ${tone.text}`}>{tone.label}</div>
          </div>
          <MetricCell label="Site EUI" value={fmt(m.site_eui, 1)} unit={m.eui_uom} />
          <MetricCell label="Source EUI" value={fmt(m.source_eui, 1)} unit={m.eui_uom} />
          <MetricCell label="GHG" value={fmt(m.ghg_mt, 1)} unit="MT CO₂e" />
        </div>
      ) : (
        <div className={`p-4 text-sm border-b border-border ${p.metrics_status === 'pending' ? 'bg-amber-50 dark:bg-amber-950/20' : p.metrics_status === 'error' ? 'bg-rose-50 dark:bg-rose-950/20' : 'bg-muted/40'}`} data-testid={`esr-msg-${p.site_uid}`}>
          <div className="flex items-start gap-2">
            <SIcon className={`h-4 w-4 mt-0.5 shrink-0 ${sm.text}`} />
            <div>
              <div className={`font-medium ${sm.text}`}>{sm.label}</div>
              <div className="text-xs text-muted-foreground mt-0.5">{p.metrics_message}</div>
              {(p.metrics_status === 'not_connected' || p.metrics_status === 'not_mapped') && (
                <button className="text-xs underline text-primary mt-1" onClick={() => navigate('/settings?tab=energy-star')}>Open Energy Star settings →</button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* meters */}
      <div className="p-4">
        <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">Meters pushed to ENERGY STAR</div>
        <div className="grid grid-cols-3 gap-2">
          {COMMODITIES.map((c) => {
            const mm = p.meters?.[c.key];
            const Icon = c.icon;
            return (
              <div key={c.key} className="border border-border p-2" data-testid={`esr-meter-${p.site_uid}-${c.key}`}>
                <div className="flex items-center gap-1.5 text-xs font-medium">
                  <Icon className="h-3.5 w-3.5 text-muted-foreground" /> {c.label}
                </div>
                {mm ? (
                  <>
                    <div className="text-lg font-bold tabular-nums" style={{ fontFamily: 'Chivo, sans-serif' }}>{mm.months_pushed}<span className="text-xs font-normal text-muted-foreground"> mo</span></div>
                    <div className="font-mono text-[9px] text-muted-foreground truncate" title={mm.espm_meter_name}>#{mm.espm_meter_id}</div>
                  </>
                ) : (
                  <div className="text-xs text-muted-foreground/60 mt-1">—</div>
                )}
              </div>
            );
          })}
        </div>
        {m?.period_ending && (
          <div className="text-[10px] text-muted-foreground mt-2">Metrics period ending {m.period_ending} (trailing 12 months)</div>
        )}
      </div>
    </div>
  );
}

function MetricCell({ label, value, unit }) {
  return (
    <div className="p-4 text-center">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="text-2xl font-bold tabular-nums mt-1" style={{ fontFamily: 'Chivo, sans-serif' }}>{value}</div>
      <div className="text-[9px] text-muted-foreground">{unit}</div>
    </div>
  );
}
